import React from "react";

/**
 * Featured Testimonials Hero Strip
 *
 * Renders the admin-curated `featured` testimonials in a visually distinct
 * gold-accented hero band immediately after the value section. These are
 * the highest-signal proof points on the site — separate from the bulk
 * written/video testimonial grids below.
 *
 * Data shape (one item):
 *   {
 *     feature_id: string,
 *     order: number,
 *     note: string | null,
 *     kind: "written" | "video",
 *     testimonial: { id, name, review?, company?, role?, video_url?, ... }
 *   }
 *
 * If no featured rows are configured the component renders nothing — safe
 * to mount unconditionally on the landing page.
 */
export default function FeaturedTestimonialsStrip({ items = [] }) {
  const featured = (items || []).filter((f) => f?.testimonial);
  if (!featured.length) return null;

  return (
    <section
      data-testid="featured-testimonials-strip"
      className="relative py-20 px-6 bg-gradient-to-b from-[#04060c] via-[#0a0a0a] to-[#04060c] border-y border-[#D4AF37]/15"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-10">
          <span
            data-testid="featured-testimonials-eyebrow"
            className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold"
          >
            ★ Featured Client Stories
          </span>
          <span className="flex-1 h-px bg-gradient-to-r from-[#D4AF37]/40 via-[#D4AF37]/10 to-transparent" />
        </div>

        <div
          className={`grid gap-6 ${
            featured.length === 1
              ? "grid-cols-1 max-w-3xl mx-auto"
              : featured.length === 2
              ? "md:grid-cols-2"
              : "md:grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {featured.map((f) => {
            const t = f.testimonial;
            const isVideo = f.kind === "video";
            return (
              <article
                key={f.feature_id}
                data-testid={`featured-testimonial-${f.feature_id}`}
                className="relative bg-gradient-to-br from-[#0a0a0a] to-[#04060c] border border-[#D4AF37]/25 rounded-2xl p-7 md:p-9 hover:border-[#D4AF37]/55 transition-colors duration-300 shadow-[0_8px_40px_-12px_rgba(212,175,55,0.18)]"
              >
                {/* Corner star accent */}
                <div className="absolute -top-3 -right-3 bg-gradient-to-br from-[#F9E5C0] via-[#D4AF37] to-[#997A15] text-black w-9 h-9 rounded-full flex items-center justify-center text-[15px] font-bold shadow-lg">
                  ★
                </div>

                {isVideo && t.video_url ? (
                  <div className="mb-5 aspect-video rounded-xl overflow-hidden bg-black/60 border border-white/5">
                    <iframe
                      src={t.video_url.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/")}
                      title={`${t.name} testimonial`}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : null}

                {t.review && (
                  <blockquote className="text-white/85 leading-relaxed text-base md:text-lg font-light italic mb-6">
                    <span className="text-[#D4AF37] text-3xl leading-none mr-1 align-top">“</span>
                    {t.review.length > 360 ? `${t.review.slice(0, 360)}…` : t.review}
                    <span className="text-[#D4AF37] text-3xl leading-none ml-1 align-bottom">”</span>
                  </blockquote>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-white/8">
                  <div>
                    <div className="text-white font-semibold text-sm" data-testid="featured-testimonial-name">
                      {t.name}
                    </div>
                    {(t.role || t.company) && (
                      <div className="text-white/55 text-xs mt-0.5">
                        {[t.role, t.company].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                  <div className="text-[#D4AF37] text-xs uppercase tracking-[0.22em] font-semibold">
                    {isVideo ? "Watch" : "Read"}
                  </div>
                </div>

                {f.note && (
                  <div className="mt-3 text-[11px] text-[#D4AF37]/70 uppercase tracking-[0.18em]">
                    {f.note}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
